import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dataverseClient, TABLES, activeOnly } from '@shared/api';
import { escapeODataString } from '@shared/api/dataverse/utils';
import { assertGuid } from '@shared/lib';
import type { ManagedList, ManagedListItem } from '../model/types';
import { compareManagedListItems } from '../model/placeholders';

// =============================================================================
// Entity Interfaces
// =============================================================================

interface ManagedListEntity {
  vba_managedlistid: string;
  vba_name: string;
  vba_displayorder?: number;
  vba_vendorsubcategorycode?: number | null;
  vba_includeotheroption?: boolean | null;
  vba_groupby?: string | null;
  vba_axisrole?: string | null;
  createdon?: string;
  modifiedon?: string;
}

interface ManagedListItemEntity {
  vba_managedlistitemid: string;
  vba_name: string;  // Correct field name (not vba_value)
  vba_displayorder?: number;
  vba_color?: string;
  vba_sourceaccountid?: string | null;
  _vba_managedlist_value?: string;  // Correct lookup field name
}

// =============================================================================
// Mappers
// =============================================================================

function mapToManagedList(entity: ManagedListEntity): ManagedList {
  return {
    id: entity.vba_managedlistid as string,
    name: entity.vba_name as string,
    displayOrder: (entity.vba_displayorder as number) || 0,
    vendorSubcategoryCode: entity.vba_vendorsubcategorycode ?? undefined,
    includeOtherOption: entity.vba_includeotheroption ?? false,
    groupBy: entity.vba_groupby ?? undefined,
    axisRole: entity.vba_axisrole ?? undefined,
    createdOn: (entity.createdon as string | undefined) ?? undefined,
    modifiedOn: (entity.modifiedon as string | undefined) ?? undefined,
    items: [],
  };
}

function mapToManagedListItem(entity: ManagedListItemEntity): ManagedListItem {
  return {
    id: entity.vba_managedlistitemid,
    listId: entity._vba_managedlist_value ?? '',
    value: entity.vba_name,
    displayOrder: entity.vba_displayorder ?? 0,
    color: entity.vba_color ?? undefined,
    sourceAccountId: entity.vba_sourceaccountid ?? undefined,
  };
}

function mapListToEntity(list: Partial<ManagedList>): Record<string, unknown> {
  const entity: Record<string, unknown> = {};
  if (list.name !== undefined) entity.vba_name = list.name;
  if (list.displayOrder !== undefined) entity.vba_displayorder = list.displayOrder;
  if (list.vendorSubcategoryCode !== undefined) {
    entity.vba_vendorsubcategorycode = list.vendorSubcategoryCode === null ? null : list.vendorSubcategoryCode;
  }
  if (list.includeOtherOption !== undefined) {
    entity.vba_includeotheroption = !!list.includeOtherOption;
  }
  if (list.groupBy !== undefined) {
    entity.vba_groupby = list.groupBy === '' ? null : list.groupBy;
  }
  if (list.axisRole !== undefined) {
    entity.vba_axisrole = list.axisRole === '' ? null : list.axisRole;
  }
  return entity;
}

function mapItemToEntity(item: Partial<ManagedListItem>, listId?: string): Record<string, unknown> {
  const entity: Record<string, unknown> = {};
  if (item.value !== undefined) entity.vba_name = item.value;  // Map value to vba_name
  if (item.displayOrder !== undefined) entity.vba_displayorder = item.displayOrder;
  // vba_color requires provisioning — only send when non-empty to avoid 400 if column doesn't exist yet
  if (item.color) entity.vba_color = item.color;
  if (item.sourceAccountId !== undefined) entity.vba_sourceaccountid = item.sourceAccountId || null;
  if (listId) entity['vba_ManagedList@odata.bind'] = `/vba_managedlists(${assertGuid(listId, 'listId')})`;
  return entity;
}

// =============================================================================
// Vendor subcategory choices — live from the Dynamics global option set
// =============================================================================

/**
 * Account column whose choice drives the managed-list "Dynamics Source".
 * Multi-select choice — a vendor account can belong to more than one
 * subcategory. History: single-select `dts_vendorpartnersubcategory`
 * (deprecated 2026-06-10) → interim `crfc2_vendorpartnersubcategories`
 * (deleted) → `dts_vendorpartnersubcategory1`, recreated in the Veracity
 * Master Solution 2026-06-12. Same `dts_vendorpartnersubcategories` global
 * option set throughout, so stored codes are unaffected.
 * Keep in sync with VENDOR_SUBCATEGORY_FIELD in features/vendor-sync.
 */
const VENDOR_SUBCATEGORY_ATTRIBUTE = 'dts_vendorpartnersubcategory1';

export interface VendorSubcategoryOption {
  code: number;
  label: string;
}

interface OptionMetadata {
  Value: number;
  Label?: { UserLocalizedLabel?: { Label?: string } | null } | null;
}

interface PicklistAttributeMetadataResponse {
  GlobalOptionSet?: { Options?: OptionMetadata[] } | null;
}

/**
 * Fetch the live vendor-subcategory choices from Dynamics metadata so the
 * managed-list source dropdown always matches Dynamics rather than a
 * hand-maintained snapshot (which drifted: it was missing 7 of the 15 choices
 * and several labels were stale).
 *
 * Anchored on the `account.dts_vendorpartnersubcategory1` attribute and its
 * expanded GlobalOptionSet — this avoids having to know the global option
 * set's own name (which need not match the column name). The field is a
 * multi-select choice, so it casts to MultiSelectPicklistAttributeMetadata;
 * the GlobalOptionSet/Options shape is identical to a single-select picklist.
 * Sorted by label.
 */
export async function fetchVendorSubcategoryOptions(): Promise<VendorSubcategoryOption[]> {
  const res = await dataverseClient.fetchRaw<PicklistAttributeMetadataResponse>(
    `EntityDefinitions(LogicalName='account')` +
      `/Attributes(LogicalName='${VENDOR_SUBCATEGORY_ATTRIBUTE}')` +
      `/Microsoft.Dynamics.CRM.MultiSelectPicklistAttributeMetadata` +
      `?$select=LogicalName&$expand=GlobalOptionSet($select=Options)`,
  );
  return (res.GlobalOptionSet?.Options ?? [])
    .map((o) => ({
      code: o.Value,
      label: o.Label?.UserLocalizedLabel?.Label ?? `Code ${o.Value}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// =============================================================================
// API Functions - Lists
// =============================================================================

export async function fetchManagedLists(): Promise<ManagedList[]> {
  const response = await dataverseClient.list<ManagedListEntity>(
    TABLES.MANAGED_LIST,
    { filter: activeOnly(), orderBy: 'vba_displayorder asc' }
  );
  return response.value.map(mapToManagedList);
}

export async function fetchManagedList(id: string): Promise<ManagedList> {
  const entity = await dataverseClient.get<ManagedListEntity>(TABLES.MANAGED_LIST, id);
  return mapToManagedList(entity);
}

export async function createManagedList(list: Partial<ManagedList>): Promise<ManagedList> {
  const entity = mapListToEntity(list);
  const response = await dataverseClient.create<ManagedListEntity>(TABLES.MANAGED_LIST, entity);
  return mapToManagedList(response);
}

export async function updateManagedList(id: string, list: Partial<ManagedList>): Promise<void> {
  const entity = mapListToEntity(list);
  await dataverseClient.update(TABLES.MANAGED_LIST, id, entity);
}

export async function deleteManagedList(id: string): Promise<void> {
  await dataverseClient.deactivate(TABLES.MANAGED_LIST, id);
}

export async function fetchManagedListByName(name: string): Promise<ManagedList | null> {
  const response = await dataverseClient.list<ManagedListEntity>(
    TABLES.MANAGED_LIST,
    { filter: activeOnly(`vba_name eq '${escapeODataString(name)}'`), top: 1 }
  );
  return response.value.length > 0 ? mapToManagedList(response.value[0]!) : null;
}

export async function fetchManagedListItemsByName(listName: string): Promise<ManagedListItem[]> {
  const list = await fetchManagedListByName(listName);
  if (!list) return [];
  return fetchManagedListItems(list.id);
}

// =============================================================================
// API Functions - Items
// =============================================================================

export async function fetchManagedListItems(listId: string): Promise<ManagedListItem[]> {
  const response = await dataverseClient.list<ManagedListItemEntity>(
    TABLES.MANAGED_LIST_ITEM,
    {
      filter: activeOnly(`_vba_managedlist_value eq '${assertGuid(listId, 'listId')}'`),
      orderBy: 'vba_displayorder asc',
    }
  );
  return sortManagedListItems(response.value.map(mapToManagedListItem));
}

/**
 * Default display order for managed-list items is alphabetical (A–Z, case-insensitive),
 * so newly added/synced items slot in naturally rather than landing at the bottom.
 * The "Other" and "N/A" placeholders are always pinned last, in that order.
 */
export function sortManagedListItems(items: ManagedListItem[]): ManagedListItem[] {
  return [...items].sort(compareManagedListItems);
}

export async function createManagedListItem(item: Partial<ManagedListItem>, listId: string): Promise<ManagedListItem> {
  const entity = mapItemToEntity(item, listId);
  const response = await dataverseClient.create<ManagedListItemEntity>(TABLES.MANAGED_LIST_ITEM, entity);
  return mapToManagedListItem(response);
}

export async function updateManagedListItem(id: string, item: Partial<ManagedListItem>): Promise<void> {
  const entity = mapItemToEntity(item);
  await dataverseClient.update(TABLES.MANAGED_LIST_ITEM, id, entity);
}

export async function deleteManagedListItem(id: string): Promise<void> {
  await dataverseClient.deactivate(TABLES.MANAGED_LIST_ITEM, id);
}

// =============================================================================
// React Query Hooks - Lists
// =============================================================================

export function useManagedLists() {
  return useQuery({
    queryKey: ['managedLists'],
    queryFn: fetchManagedLists,
  });
}

/**
 * Live vendor subcategory choices from Dynamics metadata. Cached for an hour —
 * a global choice changes rarely, and this only drives the source dropdown.
 */
export function useVendorSubcategoryOptions() {
  return useQuery({
    queryKey: ['vendorSubcategoryOptions'],
    queryFn: fetchVendorSubcategoryOptions,
    staleTime: 60 * 60 * 1000,
  });
}

export function useManagedList(id: string) {
  return useQuery({
    queryKey: ['managedLists', id],
    queryFn: () => fetchManagedList(id),
    enabled: !!id,
  });
}

export function useCreateManagedList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createManagedList,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managedLists'] });
    },
  });
}

export function useUpdateManagedList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, list }: { id: string; list: Partial<ManagedList> }) =>
      updateManagedList(id, list),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managedLists'] });
    },
  });
}

export function useDeleteManagedList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteManagedList,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managedLists'] });
    },
  });
}

export async function fetchAllManagedListItemCount(): Promise<number> {
  // Server-side count via $count=true + $top=0; previously this fetched the
  // entire active table just to call .length, which scaled linearly with
  // managed-list size.
  const response = await dataverseClient.list<ManagedListItemEntity>(
    TABLES.MANAGED_LIST_ITEM,
    {
      select: ['vba_managedlistitemid'],
      filter: activeOnly(),
      top: 0,
      count: true,
    }
  );
  return response['@odata.count'] ?? 0;
}

export function useManagedListItemCount() {
  return useQuery({
    queryKey: ['managedListItemCount'],
    queryFn: fetchAllManagedListItemCount,
  });
}

// =============================================================================
// React Query Hooks - Items
// =============================================================================

export function useManagedListItems(listId: string) {
  return useQuery({
    queryKey: ['managedListItems', listId],
    queryFn: () => fetchManagedListItems(listId),
    enabled: !!listId,
  });
}

export function useManagedListItemsByName(listName: string) {
  return useQuery({
    queryKey: ['managedListItemsByName', listName],
    queryFn: () => fetchManagedListItemsByName(listName),
    enabled: !!listName,
    staleTime: 10 * 60 * 1000,
  });
}

export function useCreateManagedListItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ item, listId }: { item: Partial<ManagedListItem>; listId: string }) =>
      createManagedListItem(item, listId),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({ queryKey: ['managedListItems', listId] });
      queryClient.invalidateQueries({ queryKey: ['managedLists'] });
    },
  });
}

export function useUpdateManagedListItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, item }: { id: string; item: Partial<ManagedListItem> }) =>
      updateManagedListItem(id, item),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managedListItems'] });
    },
  });
}

export function useDeleteManagedListItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; listId: string }) =>
      deleteManagedListItem(id),
    onSuccess: (_data, { listId }) => {
      queryClient.invalidateQueries({ queryKey: ['managedListItems', listId] });
      queryClient.invalidateQueries({ queryKey: ['managedLists'] });
    },
  });
}
